<!-- A generative AI model wrote or edited portions of this file with the supervision of a human developer and careful human review. -->
<script>
  import { Tooltip } from "@urbaninstitute/dataviz-components";
  import { ColorLegend } from "@urbaninstitute/dataviz-components/maps";
  import { urbanColors } from "@urbaninstitute/dataviz-components/utils";
  import MapLibre from "$components/MapLibre/MapLibre.svelte";
  import { useData } from "$lib/state/data.svelte.js";
  import { useMapData } from "$lib/state/map.svelte.js";
  import { getName } from "$utils/geo-names.js";
  import { scaleThreshold, scaleLinear, scaleQuantile } from "d3-scale";
  import { jenks } from "simple-statistics";
  import { formatValue, formatShortValue } from "$utils/formats.js";
  import { formatDate } from "$utils/dates.js";

  // props
  /** @type {{ data: any, zoomable?: boolean, onready?: () => void }} */
  let { data, zoomable = true, onready = () => {} } = $props();

  // consts

  // colors to use for the map
  const MAP_COLORS = [
    urbanColors.blue_shade_lightest,
    urbanColors.blue_shade_light,
    urbanColors.blue,
    urbanColors.blue_shade_dark,
    urbanColors.blue_shade_darkest
  ];

  // state

  // global data state
  let dataState = useData();
  // map data state
  let mapData = useMapData();
  // holds the map container element
  let mapEl = $state();

  /**
   * @typedef {{ title: string, date: string, value: string, position: number[] }} MapTooltip
   */

  /**
   * Tooltip state
   * @type {MapTooltip|null}
   */
  let mapTooltip = $state(null);

  // derived state

  // are all of the map data layers loaded?
  let mapDataReady = $derived(mapData.stateFeatures !== null && mapData.districtFeatures !== null);

  // is the map showing districts or states?
  let showDistricts = $derived(dataState.currentLevel === "district");

  // aria label for the map
  let mapAriaLabel = $derived(
    `Choropleth map showing ${dataState.currentMetadata?.long_label ?? "data"} by ${showDistricts ? "congressional district" : "state"}`
  );

  // current map feature to highlight
  let activeGeo = $derived(
    (dataState.currentLevel === "district" ? dataState.currentDistrict : dataState.currentState) ??
      ""
  );

  // frequence of selected metric
  let dataFrequency = $derived(dataState.currentMetadata?.frequency);

  // map color scale based on selected data
  let mapColorScale = $derived.by(() => {
    let skipKeys = ["date", "series"];
    let allKeys = Object.keys(data).filter((k) => !skipKeys.includes(k));
    let allValues = allKeys.map((k) => data[k]);
    return getJenksScale(allValues, MAP_COLORS);
    // return scaleThreshold().domain([128400, 357600, 1029600, 3885000]).range(MAP_COLORS);
  });

  // Create fill function for MapLibre
  let fillFunction = $derived.by(() => {
    return (/** @type {string} */ geoid) => getGeoColor(geoid);
  });

  /**
   * Sets the geo state based on the geoid.
   * @param {string} geoid - The geographic identifier for the county.
   */
  function setGeo(geoid) {
    if (geoid.length === 2) {
      dataState.currentState = geoid;
      return;
    }
    // if its a district, set state and district, just to be safe
    dataState.currentState = geoid.slice(0, 2);
    dataState.currentDistrict = geoid;
  }

  /**
   * @param {string} geoid
   */
  function getGeoColor(geoid) {
    if (Object.hasOwn(data, geoid)) {
      return mapColorScale(data[geoid]);
    }
    return urbanColors.gray_shade_lighter;
  }

  /**
   * @param {any} rawBreak
   */
  function roundBreak(rawBreak) {
    // Round the breaks
    return rawBreak;
  }

  /**
   * @param {MouseEvent} event
   * @param {any} detail
   */
  function handleMousemove(event, detail) {
    mapTooltip = {
      title: getName(detail.geoid) || "",
      date: formatDate(data.date, dataFrequency),
      value: formatValue(data[detail.geoid], dataState.currentMetadata),
      position: [event.pageX, event.pageY]
    };
  }

  function clearTooltip() {
    mapTooltip = null;
  }

  /**
   * @param {number[]} domain
   * @param {string[]} colors
   * @returns {import("d3-scale").ScaleThreshold<number, string, string>}
   */
  function getJenksScale(domain, colors) {
    if (domain.length === 0) {
      return /** @type {any} */ (
        scaleThreshold()
          .domain([0])
          .range(/** @type {any} */ ([urbanColors.gray_shade_lighter]))
      );
    }
    domain.sort();
    const breaks = jenks(domain, colors.length);
    const thresholds = breaks.slice(1, -1).map(roundBreak);
    const result = /** @type {import("d3-scale").ScaleThreshold<number, string, string>} */ (
      scaleThreshold(thresholds, colors)
    );
    return result;
  }
</script>

<div class="map-container" bind:this={mapEl}>
  {#if mapDataReady}
    {#if mapColorScale}
      <div class="legend-wrap">
        <ColorLegend
          scale={mapColorScale}
          height={16}
          tickFormat={(d) => formatShortValue(d, dataState.currentMetadata)}
          naFill={urbanColors.gray_shade_lighter}
          naLabel="N/A"
          margin={{ top: 0, right: 10, bottom: 10, left: 0 }}
        />
      </div>
    {/if}

    {#if showDistricts}
      <MapLibre
        features={mapData.districtFeatures}
        outlineFeatures={mapData.stateFeatures}
        {data}
        fill={fillFunction}
        {zoomable}
        stroke={urbanColors.white}
        strokeWidth={0.5}
        hoverStroke={urbanColors.yellow}
        hoverStrokeWidth={2}
        highlightFeature={{ geoid: activeGeo }}
        zoomTarget={activeGeo ? { geoid: activeGeo } : null}
        {onready}
        ariaLabel={mapAriaLabel}
        onmousemove={(/** @type {any} */ detail) => handleMousemove(detail.event, detail)}
        onmouseout={clearTooltip}
        onclick={(/** @type {any} */ detail) => setGeo(detail.geoid)}
      />
    {:else}
      <MapLibre
        features={mapData.stateFeatures}
        {data}
        fill={fillFunction}
        {zoomable}
        stroke={urbanColors.white}
        strokeWidth={0.5}
        hoverStroke={urbanColors.yellow}
        hoverStrokeWidth={2}
        highlightFeature={{ geoid: activeGeo }}
        zoomTarget={activeGeo ? { geoid: activeGeo } : null}
        {onready}
        ariaLabel={mapAriaLabel}
        onmousemove={(/** @type {any} */ detail) => handleMousemove(detail.event, detail)}
        onmouseout={clearTooltip}
        onclick={(/** @type {any} */ detail) => setGeo(detail.geoid)}
      />
    {/if}
  {:else}
    <div class="loading-container">
      <p>Loading...</p>
    </div>
  {/if}
  {#if mapTooltip}
    <Tooltip x={mapTooltip.position[0]} y={mapTooltip.position[1]}>
      <div class="tooltip-title">{mapTooltip.title}</div>
      <div class="tooltip-date">{mapTooltip.date}</div>
      <div class="tooltip-value">{mapTooltip.value}</div>
    </Tooltip>
  {/if}
  <div class="sr-only" aria-live="polite" role="status">
    {#if mapTooltip}
      {mapTooltip.title}: {mapTooltip.value} ({mapTooltip.date})
    {/if}
  </div>
</div>

<style>
  .map-container {
    position: relative;
    margin-block-start: var(--spacing-8);
  }
  .loading-container {
    background: var(--color-gray-shade-lighter);
    display: flex;
    min-height: 18rem;
    align-items: center;
    justify-content: center;
  }

  .map-container :global(.legend-label) {
    font-size: var(--font-size-small) !important;
    line-height: 21px !important;
    margin-bottom: var(--spacing-4);
    font-style: italic;
  }

  .tooltip-title,
  .tooltip-value {
    font-size: 14px;
    font-weight: var(--font-weight-bold) !important;
    margin-bottom: var(--spacing-1);
    line-height: 18px;
  }
  .tooltip-date {
    font-size: 14px;
    font-style: italic;
    margin-bottom: var(--spacing-1);
    line-height: 18px;
  }

  @media print {
    :global(.legend-label) {
      font-size: 12px;
      line-height: 16px;
      margin-bottom: var(--spacing-2);
    }
  }

  .legend-wrap {
    width: 100%;
    max-width: 350px;
    margin-inline: auto;
  }
  :global(.print-wrap .legend-wrap) {
    margin-top: 1rem;
    width: 100%;
  }
  @media (min-width: 1024px) {
    .map-container {
      padding-bottom: 45px;
    }
  }

  .sr-only {
    position: absolute;
    width: 1px;
    height: 1px;
    padding: 0;
    margin: -1px;
    overflow: hidden;
    clip: rect(0, 0, 0, 0);
    white-space: nowrap;
    border-width: 0;
  }
</style>
